import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || 'all';
  const [filteredArticles, setFilteredArticles] = useState(articles);

  useEffect(() => {
    if (activeCat === 'all') {
      setFilteredArticles(articles);
    } else {
      setFilteredArticles(articles.filter((a) => a.category === activeCat));
    }
  }, [activeCat]);

  const handleCatChange = (slug: string) => {
    if (slug === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ cat: slug });
    }
  };

  const activeCategoryName = activeCat === 'all' ? '全部文章' : categories.find((c) => c.slug === activeCat)?.name || '全部文章';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategoryName} - po文网`,
    description: `浏览po文网的${activeCategoryName}分类，发现优质原创文章`,
    url: `https://arkfilm.cn/category${activeCat !== 'all' ? `?cat=${activeCat}` : ''}`,
  };

  return (
    <>
      <SEOHead
        title={`${activeCategoryName} - 文章分类`}
        description={`浏览po文网的${activeCategoryName}分类，海量优质原创内容等你发现。po文网汇聚众多创作者的精彩文章，涵盖科技、生活、文化、职场等多个领域。`}
        keywords={`po文网,${activeCategoryName},原创文章,写作社区,po文网分类`}
        canonicalUrl={`https://arkfilm.cn/category${activeCat !== 'all' ? `?cat=${activeCat}` : ''}`}
        schema={pageSchema}
      />

      <div className="min-h-screen pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '文章分类' }]} />

            <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mt-4 mb-2">
              文章分类
            </h1>
            <p className="text-sm md:text-base text-foreground-600">
              探索不同领域的原创内容，发现你感兴趣的精彩文章
            </p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="sticky top-16 z-40 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide flex-wrap">
              <button
                onClick={() => handleCatChange('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCat === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-primary-600 hover:bg-primary-50'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCatChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCat === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600 hover:bg-primary-50'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Articles Grid */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          {filteredArticles.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-foreground-600">
                  共 <strong className="text-foreground-900">{filteredArticles.length}</strong> 篇文章
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
                <i className="ri-inbox-line text-3xl"></i>
              </div>
              <p className="text-foreground-600">该分类暂无文章，敬请期待</p>
              <Link
                to="/category"
                className="inline-flex items-center gap-1 mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 查看全部文章
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}